import BottomSheet from 'components/BottomSheet';
import Button, { Size, Variants } from 'components/Button';
import Confirmation from 'components/Confirmation';
import EditDateApproved from 'components/EditDateApproved';
import useModal from 'hooks/useModal';
import { approve, reject } from 'queries/job';
import React from 'react';
import toast from 'react-hot-toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { readAxiosErr } from 'utils/misc';
import { formatServerTime } from 'utils/time';
import Checkbox from './Checkbox';
import Icon from 'components/Icon';
import clx from 'classnames';
import { trackEvent } from 'utils/segment';
import useAuth from 'hooks/useAuth';

type AppProps = {
  data: Record<string, any>;
  renderInTable?: boolean;
};

const Approved: React.FC<AppProps> = ({ data, renderInTable = false }) => {
  const { jobId = '' } = useParams();
  const queryClient = useQueryClient();
  const [edit, showEdit, closeEdit] = useModal();
  const [confirmOpen, showConfirmModal, closeConfirmModal] = useModal();
  const [actionOpen, showActions, closeActions] = useModal();
  const { userInfo } = useAuth();

  const approveMutation = useMutation(() => approve(jobId), {
    onError: (err: any) => {
      toast.error(readAxiosErr(err));
    },
    onSuccess: async (res: any) => {
      await Promise.all([
        queryClient.invalidateQueries(['job-last-checklist', jobId]),
        queryClient.invalidateQueries(['job-checklist', jobId]),
      ]);
      queryClient.invalidateQueries(['job-detail', jobId]);
      queryClient.invalidateQueries(['jobs']);
      trackEvent('Job Approved', {
        job_id: jobId,
      });
      trackEvent(
        'Job Approved',
        {
          job_id: jobId,
          job_type: res?.job_type,
          estimated_value: res?.estimate?.gt_price,
        },
        userInfo
      );
    },
  });

  const rejectMutation = useMutation(() => reject(jobId), {
    onError: (err: any) => {
      toast.error(readAxiosErr(err));
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries(['job-last-checklist', jobId]),
        queryClient.invalidateQueries(['job-checklist', jobId]),
      ]);
      queryClient.invalidateQueries(['job-detail', jobId]);
      queryClient.invalidateQueries(['jobs']);
    },
  });

  const incomplete = () => {
    return (
      <div className="">
        <div className="apart">
          <div className="value">Not Approved</div>
          {data?.permissions?.can_approve && (
            <div>
              <Button
                variant={Variants.Tag}
                label="Approve"
                size={Size.Small}
                onClick={() => approveMutation.mutate()}
                loading={approveMutation.isLoading}
              />
            </div>
          )}
        </div>
        {!!data?.message && (
          <div className="text-xs text-error">* {data?.message}</div>
        )}
      </div>
    );
  };

  const complete = () => {
    return (
      <div>
        <div className="apart">
          <div className="value">Approved</div>
          <div>
            <Button
              variant={Variants.Tag}
              label="Actions"
              size={Size.Small}
              onClick={showActions}
            />
          </div>
        </div>
        <div className="-mt-1">
          <span className="key">Approved By: </span>
          <span className="key">{data.completed_by?.name}</span>
        </div>
        <div className="-mt-1">
          <span className="key">Approved At: </span>
          <span className="key">
            {formatServerTime({
              date: data.date,
              parseFormat: 'YYYY-MM-DDThh:mm:ssZ',
              includeTime: true,
            })}
          </span>
        </div>

        <BottomSheet open={actionOpen}>
          <div className="pb-4">
            <div className="flex justify-between">
              <div className="page-heading">Actions</div>
              {!rejectMutation.isLoading && (
                <div>
                  <Icon name="close" size={32} onClick={closeActions} />
                </div>
              )}
            </div>
            <div className="mt-4 space-y-4">
              {data?.permissions?.can_edit && (
                <Button
                  label="Edit Date Approved"
                  size={Size.Big}
                  variant={Variants.Tag}
                  onClick={showEdit}
                />
              )}
              {data?.permissions?.can_reject && (
                <Button
                  label="Un-Approve Job?"
                  size={Size.Big}
                  variant={Variants.ErrorNegative}
                  onClick={showConfirmModal}
                  loading={rejectMutation.isLoading}
                />
              )}
            </div>
          </div>
        </BottomSheet>
        {edit && (
          <BottomSheet open={edit} closeSheet={closeEdit}>
            <EditDateApproved
              date_approved={data.date}
              jobId={jobId}
              closeModal={closeEdit}
            />
          </BottomSheet>
        )}
        {confirmOpen && (
          <Confirmation
            title="Unapprove Job?"
            message="Are you sure you want to unapprove job?"
            open={confirmOpen}
            closeConfirm={closeConfirmModal}
            onClick={() => rejectMutation.mutate()}
            isLoading={rejectMutation.isLoading}
          />
        )}
      </div>
    );
  };

  return (
    <div className={clx({ card: !renderInTable, 'px-2 pb-1': renderInTable })}>
      <div className="flex space-x-2">
        <Checkbox checked={data.complete} />
        <div className="w-full">
          {data.complete ? complete() : incomplete()}
        </div>
      </div>
    </div>
  );
};

export default Approved;
